// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { Card, Radio, Typography } from '@mui/material';
import { AppIcon, Input, Label, OptionsSelector } from '@shared/ui-kit/components';
import { Translation } from '@shared/ui-kit/providers';
import { defineComponent } from '@shared/ui-kit/utils/define-component';
import { classNames } from '@shared/ui-kit/utils';
import {
  NavigationControls,
  ScreenHeader,
  ScreenLayout,
} from '@shared/advisory/account-workflow/layout';
import { useProfessionalProfile } from './use-professional-profile';
import { Controller } from 'react-hook-form';
import { EmploymentStatus } from '@shared/advisory/account-workflow/types';

export const ProfessionalProfile = defineComponent({
  displayName: 'AccountOpeningProfessionalProfile',
  useSetup: useProfessionalProfile,
  View: ({
    t,
    form,
    onNext,
    onEmploymentStatusChange,
    isFormValid,
    employmentStatus,
    employmentStatusOptions,
    sectorOptions,
    activityOptions,
    sector,
  }) => (
    <ScreenLayout
      actions={
        <NavigationControls
          form="professional-profile-form"
          isFormValid={isFormValid}
        />
      }
    >
      <div className="w-full">
        <form id="professional-profile-form" onSubmit={form.handleSubmit(onNext)}>
          <fieldset className="m-0 flex min-w-0 flex-col gap-7 border-0 p-0">
            <ScreenHeader
              title={
                <Translation
                  i18nKey="account-opening.screens.professional-profile.title"
                  ns="components"
                />
              }
              subtitle={
                <Translation
                  i18nKey="account-opening.screens.professional-profile.subtitle"
                  ns="components"
                />
              }
              className="mb-7"
            />

            <Card className="!rounded-xl !p-6" variant="outlined">
              <div className="flex flex-col gap-5">
                <Typography
                  variant="h5"
                  className="!text-h5 !font-semibold !text-title-navy"
                  data-testid="employment-status-title"
                >
                  <Translation
                    i18nKey="account-opening.professional-profile.employment-status.title"
                    ns="components"
                  />
                </Typography>

                <div className="flex flex-col gap-4">
                  <Controller
                    name="employmentStatus"
                    control={form.control}
                    render={({ field }) => (
                      <>
                        {employmentStatusOptions.map(option => {
                          const isSelected = field.value === option.value;

                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => onEmploymentStatusChange(option.value)}
                              className={classNames(
                                'flex w-full items-center gap-4 rounded-2xl border border-border-light bg-white p-4 text-left transition-all hover:border-primary hover:bg-primary/5',
                                {
                                  'border-primary bg-primary/5 ring-1 ring-primary/20':
                                    isSelected,
                                },
                              )}
                              data-testid={`employment-status-option-${option.value}`}
                            >
                              <div
                                className={classNames(
                                  'flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-colors',
                                  isSelected ? 'bg-primary' : 'bg-page',
                                )}
                              >
                                <AppIcon
                                  icon={option.icon}
                                  size="lg"
                                  color={isSelected ? 'light' : 'gray'}
                                />
                              </div>

                              <div className="flex min-w-0 flex-1 flex-col items-start justify-center">
                                <Typography
                                  variant="h5"
                                  className="!mb-1 !text-base !font-semibold !text-title-navy"
                                >
                                  {option.label}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  className="!text-sm !text-subtitle"
                                >
                                  <Translation
                                    i18nKey={option.subtitleKey as any}
                                    ns="components"
                                  />
                                </Typography>
                              </div>

                              <Radio
                                checked={isSelected}
                                className="!p-0 !text-primary hover:!bg-transparent"
                              />
                            </button>
                          );
                        })}
                      </>
                    )}
                  />
                </div>
              </div>
            </Card>

            {employmentStatus === EmploymentStatus.Employed && (
              <Card className="!rounded-xl !p-6" variant="outlined">
                <div className="flex flex-col gap-6">
                  <Typography
                    variant="h5"
                    className="!text-h5 !font-semibold !text-title-navy"
                    data-testid="employed-detail-title"
                  >
                    <Translation
                      i18nKey="account-opening.professional-profile.employed-detail.title"
                      ns="components"
                    />
                  </Typography>

                  <Typography
                    variant="body2"
                    className="!text-sm !font-normal !text-subtitle"
                    data-testid="employed-detail-hint"
                  >
                    <Translation
                      i18nKey="account-opening.professional-profile.employed-detail.hint"
                      ns="components"
                    />
                  </Typography>

                  <div className="flex flex-col gap-6">
                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="company-name-input" required>
                        <Translation
                          i18nKey="account-opening.professional-profile.employed-detail.company-name"
                          ns="components"
                        />
                      </Label>

                      <Controller
                        name="companyName"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <Input
                            {...field}
                            id="company-name-input"
                            placeholder={t(
                              'account-opening.professional-profile.employed-detail.company-name-placeholder',
                              { ns: 'components' },
                            )}
                            data-testid="company-name-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="sector-selector" required>
                        <Translation
                          i18nKey="account-opening.professional-profile.employed-detail.sector"
                          ns="components"
                        />
                      </Label>

                      <Controller
                        name="sector"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <OptionsSelector
                            optionsAvailable={sectorOptions}
                            onChangeValueSelected={field.onChange}
                            optionSelected={field.value}
                            defaultText={t(
                              'account-opening.professional-profile.employed-detail.sector-placeholder',
                              { ns: 'components' },
                            )}
                            selectType="white"
                            dataTestId="sector-selector"
                            error={!!fieldState.error}
                            searchBar
                            required
                          />
                        )}
                      />
                    </fieldset>

                    {sector && (
                      <fieldset className="col-span-1 grid gap-2">
                        <Label htmlFor="company-activity-selector" required>
                          <Translation
                            i18nKey="account-opening.professional-profile.employed-detail.company-activity"
                            ns="components"
                          />
                        </Label>

                        <Controller
                          name="companyActivity"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <OptionsSelector
                              optionsAvailable={activityOptions}
                              onChangeValueSelected={field.onChange}
                              optionSelected={field.value}
                              defaultText={t(
                                'account-opening.professional-profile.employed-detail.company-activity-placeholder',
                                { ns: 'components' },
                              )}
                              selectType="white"
                              dataTestId="company-activity-selector"
                              error={!!fieldState.error}
                              searchBar
                              required
                            />
                          )}
                        />
                      </fieldset>
                    )}
                  </div>
                </div>
              </Card>
            )}

            {employmentStatus === EmploymentStatus.SelfEmployed && (
              <Card className="!rounded-xl !p-6" variant="outlined">
                <div className="flex flex-col gap-6">
                  <Typography
                    variant="h5"
                    className="!text-h5 !font-semibold !text-title-navy"
                    data-testid="self-employed-detail-title"
                  >
                    <Translation
                      i18nKey="account-opening.professional-profile.self-employed-detail.title"
                      ns="components"
                    />
                  </Typography>

                  <div className="flex flex-col gap-6">
                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="business-name-input" required>
                        <Translation
                          i18nKey="account-opening.professional-profile.self-employed-detail.business-name"
                          ns="components"
                        />
                      </Label>

                      <Controller
                        name="businessName"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <Input
                            id="business-name-input"
                            {...field}
                            placeholder={t(
                              'account-opening.professional-profile.self-employed-detail.business-name-placeholder',
                              { ns: 'components' },
                            )}
                            data-testid="business-name-input"
                            required
                            error={!!fieldState.error}
                          />
                        )}
                      />
                    </fieldset>

                    <fieldset className="col-span-1 grid gap-2">
                      <Label htmlFor="self-employed-sector-selector" required>
                        <Translation
                          i18nKey="account-opening.professional-profile.self-employed-detail.sector"
                          ns="components"
                        />
                      </Label>

                      <Controller
                        name="sector"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <OptionsSelector
                            optionsAvailable={sectorOptions}
                            onChangeValueSelected={field.onChange}
                            optionSelected={field.value}
                            defaultText={t(
                              'account-opening.professional-profile.self-employed-detail.sector-placeholder',
                              { ns: 'components' },
                            )}
                            selectType="white"
                            dataTestId="self-employed-sector-selector"
                            error={!!fieldState.error}
                            searchBar
                            required
                          />
                        )}
                      />
                    </fieldset>

                    {sector && (
                      <fieldset className="col-span-1 grid gap-2">
                        <Label htmlFor="main-activity-selector" required>
                          <Translation
                            i18nKey="account-opening.professional-profile.self-employed-detail.main-activity"
                            ns="components"
                          />
                        </Label>

                        <Controller
                          name="mainActivity"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <OptionsSelector
                              optionsAvailable={activityOptions}
                              onChangeValueSelected={field.onChange}
                              optionSelected={field.value}
                              defaultText={t(
                                'account-opening.professional-profile.self-employed-detail.main-activity-placeholder',
                                { ns: 'components' },
                              )}
                              selectType="white"
                              dataTestId="main-activity-selector"
                              error={!!fieldState.error}
                              searchBar
                              required
                            />
                          )}
                        />
                      </fieldset>
                    )}
                  </div>
                </div>
              </Card>
            )}
          </fieldset>
        </form>
      </div>
    </ScreenLayout>
  ),
});
